import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, ConfirmDialog, formatDateTime, formatDateWithYear } from '@shared-ui/components';
import { SUBJECT_NAMES } from '@padcanvas/shared';
import { adminApi } from '../../../api/adminApi';
import {
  billingApi, PLAN_LABEL, type MoveResult, type PurchasablePlan,
} from '../../../api/billingApi';
import '../../subscriptions/Billing.css';

/**
 * A user's plans and use as the billing model sees them (GET console/users/{id}/billing),
 * and the move off full access onto Basic (+ a month of Plus or Pro).
 *
 * Design: docs/FeatureDesigns/billing/subscriptions-and-payments-target.md §10, §11.
 */

type MoveChoice = 'basic' | PurchasablePlan;

const MOVE_LABEL: Record<MoveChoice, string> = {
  basic: 'Basic only',
  plus: 'Basic + a month of Plus',
  pro: 'Basic + a month of Pro',
};

const td = { padding: '6px 10px' } as const;

export function BillingPanel({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<MoveChoice>('basic');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<MoveResult | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['userBilling', userId],
    queryFn: () => billingApi.getUserBilling(userId),
    enabled: !!userId,
  });

  // The resolved `billing_plans` gate: on moves the student onto plans at their
  // next app open; off gives full access back. Same query as the Feature Gates tab.
  const { data: gates } = useQuery({
    queryKey: ['userFeatureGates', userId],
    queryFn: () => adminApi.getUserFeatureGates(userId),
    enabled: !!userId,
  });
  const billingGate = gates?.gates.find(g => g.name === 'billing_plans');

  const move = useMutation({
    mutationFn: () => billingApi.moveUserToPlans(userId, choice === 'basic' ? null : choice),
    onSuccess: res => {
      setResult(res);
      setConfirming(false);
      queryClient.invalidateQueries({ queryKey: ['userBilling', userId] });
      queryClient.invalidateQueries({ queryKey: ['userSubscriptions', userId] });
      queryClient.invalidateQueries({ queryKey: ['userEligibility', userId] });
      queryClient.invalidateQueries({ queryKey: ['userFeatureGates', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  return (
    <div className="bill-panel">
      <div className="bill-panel-head">
        <div>
          <h4>Billing</h4>
          <span className="bill-sub">Plans and today's use, as the student's subscription page sees them.</span>
        </div>
        <div className="bill-move">
          <select
            value={choice}
            onChange={e => setChoice(e.target.value as MoveChoice)}
            aria-label="Plan to move onto"
            style={{ padding: '6px 10px', border: '1px solid var(--border-color-dark)', borderRadius: 6, fontSize: 13 }}
          >
            {(Object.keys(MOVE_LABEL) as MoveChoice[]).map(k => <option key={k} value={k}>{MOVE_LABEL[k]}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={() => { move.reset(); setConfirming(true); }}>
            Move to plans
          </Button>
        </div>
      </div>

      {result && (
        <div className="bill-result">
          Moved{result.bypassWas ? ' off full access' : ''}: {result.basicGranted ? 'Basic granted' : 'already had Basic'}
          {result.plan && <>, a month of {PLAN_LABEL[result.plan]}</>}
          {result.orderId && (
            <> — order <Link className="bill-mono" to={`/billing/orders?order=${encodeURIComponent(result.orderId)}`}>{result.orderId}</Link></>
          )}
          . Legacy plans cancelled: {result.cancelledLegacy}.
        </div>
      )}

      {isLoading ? (
        <div className="loading">Loading billing...</div>
      ) : error ? (
        <p className="bill-error">Error: {(error as Error).message}</p>
      ) : data && (
        <>
          <div className="bill-panel-facts">
            {data.fullAccess
              ? <Badge variant="warning" size="sm">Full access (bypass) — never metered</Badge>
              : <Badge variant="default" size="sm">Metered</Badge>}
            <span>
              Current plan: <strong>{data.currentTier ? PLAN_LABEL[data.currentTier] : 'none'}</strong>
            </span>
            {billingGate && (
              <Badge
                variant={billingGate.resolved ? 'info' : 'default'}
                size="sm"
                title={billingGate.userOverride === null ? 'Not overridden for this user' : 'Overridden for this user'}
              >
                billing_plans gate: {billingGate.resolved ? 'on' : 'off'}
                {billingGate.userOverride === null ? ' (default)' : ''}
              </Badge>
            )}
            <span>Moved: <strong>{data.movedAt ? formatDateTime(data.movedAt) : 'never'}</strong></span>
            {data.trial && (
              <span>
                Plus trial: <strong>{data.trial.used} of {data.trial.total}</strong> study days
              </span>
            )}
            {!data.paymentsOpen && (
              <span className="bill-sub">
                Payments not open — ₹0 orders only
                {data.renewalCode ? <> · renewal code <span className="bill-mono">{data.renewalCode}</span></> : ''}
              </span>
            )}
          </div>

          <h5>Grants (current and upcoming)</h5>
          {data.grants.length === 0 ? (
            <p className="no-features-message">No current or upcoming grants.</p>
          ) : (
            <table className="dt dt--compact">
              <thead>
                <tr>
                  <th style={td}>Plan</th>
                  <th style={td}>Subjects</th>
                  <th style={td}>Start</th>
                  <th style={td}>End</th>
                  <th style={td}>Source</th>
                  <th style={td}>When</th>
                </tr>
              </thead>
              <tbody>
                {data.grants.map(g => (
                  <tr key={g.id}>
                    <td style={td}>
                      <strong>{g.name}</strong>
                      {g.tier && <span className="bill-sub"> · {g.tier}</span>}
                    </td>
                    <td style={td}>{g.subjects.length ? g.subjects.map(s => SUBJECT_NAMES[s] || s).join(', ') : 'All'}</td>
                    <td style={td}>{formatDateWithYear(g.startDate)}</td>
                    <td style={td}>{formatDateWithYear(g.endDate)}</td>
                    <td style={td}>{g.source ?? '—'}</td>
                    <td style={td}>
                      <Badge variant={g.current ? 'success' : 'info'} size="sm">{g.current ? 'Current' : 'Upcoming'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h5>Today's use</h5>
          {data.fullAccess ? (
            <p className="no-features-message">Not metered while on full access.</p>
          ) : data.usage.length === 0 ? (
            <p className="no-features-message">Nothing used yet.</p>
          ) : (
            <table className="dt dt--compact">
              <thead>
                <tr>
                  <th style={td}>Limit</th>
                  <th style={td}>Bucket</th>
                  <th style={td}>Used / limit</th>
                  <th style={td}>Resets</th>
                </tr>
              </thead>
              <tbody>
                {data.usage.map(u => (
                  <tr key={`${u.eligibility}:${u.bucket}`}>
                    <td style={td}>{u.label}</td>
                    <td style={td}>{u.bucket === 'all' ? 'All subjects' : SUBJECT_NAMES[u.bucket] || u.bucket}</td>
                    <td style={td} className="bill-num">{u.used} / {u.limit ?? '∞'}</td>
                    <td style={td}>
                      {u.resetsAt ? formatDateTime(u.resetsAt) : 'never'}
                      <span className="bill-sub"> · {u.resetPeriod}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={confirming}
        message={
          <>
            <p style={{ marginTop: 0 }}>
              <strong>{MOVE_LABEL[choice]}</strong>
            </p>
            <p>
              This removes full access (bypass), gives Basic, optionally a month of Plus or Pro, and cancels
              the old free-ai plan. Limits apply from now.
            </p>
            <p>
              It also switches the <code>billing_plans</code> feature gate on for this user. Switching that
              gate off later gives full access back.
            </p>
            {move.isError && <p className="bill-error">{(move.error as Error).message}</p>}
          </>
        }
        confirmLabel="Move to plans"
        confirmVariant="danger"
        busy={move.isPending}
        onConfirm={() => move.mutate()}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
